import { BarDatum } from "@nivo/bar";
import React, { useRef } from "react";
import styled from "styled-components";
import { currencySymbol } from "../../formatting/currency-utils";
import formatCurrency from "../../formatting/formatCurrency";
import { formatUnits } from "../../formatting/formatKiloUnits";
import {
  ChartToolTip,
  ChartToolTipBody,
  ChartToolTipHeader,
} from "../ChartTooltip";
import { Column } from "../layout/Flex";
import Tooltip from "../Tooltip";
import BarChart from "./BarChart";
import { ChartData, getChartLegend, legendKeyColors } from "./chart-types";
import {
  Chart,
  ChartHeader,
  ChartLegend,
  ChartTooltipColorBox,
  ChartWrapper,
} from "./common";
import { NonGeneratingSiteOverlay } from "./NonGeneratingSiteOverlay";
import { NonOverflowTooltip } from "./NonOverflowTooltip";

interface ProposalSiteChartProps {
  header: string;
  chartData: ChartData[];
  toolTipText: string;
  currency: boolean;
  currencyCode: string;
  isNotYetProcuringSite: boolean;
  showNotYetImportingMessaging: boolean;
  isNotYetImportingSite: boolean;
  buyEnergyBlock: React.ReactNode;
  height: string;
}

const ProposalSiteChart: React.FC<ProposalSiteChartProps> = ({
  header,
  chartData,
  toolTipText,
  currency,
  currencyCode,
  isNotYetProcuringSite,
  showNotYetImportingMessaging,
  isNotYetImportingSite,
  buyEnergyBlock,
  height,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const chartLegend = getChartLegend(chartData);

  const showProposalSiteChartTooltip = (
    e: {
      data: BarDatum;
    },
    keys: string[],
    currency: boolean,
    currencyCode: string
  ) => {
    const headerText = e.data["index"];
    const eventCoords = e as unknown as { x: number; y: number };
    return (
      <NonOverflowTooltip
        point={{ x: eventCoords.x, y: eventCoords.y }}
        containerSize={ref.current!.getBoundingClientRect()}
      >
        <ChartToolTip>
          <ChartToolTipHeader>{headerText}</ChartToolTipHeader>
          <MonthlyTooltip
            data={e.data}
            keys={keys}
            currency={currency}
            currencyCode={currencyCode}
          />
        </ChartToolTip>
      </NonOverflowTooltip>
    );
  };

  return (
    <ChartWrapper ref={ref} height={height} $padding={"15px 0 0 0"}>
      <ProposalChartHeader>
        <ChartHeader>{header}</ChartHeader>
        <Tooltip text={toolTipText} position={"bottomLeft"} />
      </ProposalChartHeader>
      <Chart>
        <ChartLegend chartLegend={chartLegend} />
        <BarChart
          chartData={chartData as BarDatum[]}
          tooltipFn={(e) =>
            showProposalSiteChartTooltip(
              e,
              Object.keys(chartLegend),
              currency,
              currencyCode
            )
          }
          keys={Object.keys(chartLegend)}
          paddingProp={0.3}
          tickRotationProp={0}
          barColors={Object.values(chartLegend)}
          stacked={true}
          legend={currency ? currencySymbol(currencyCode) : "kWh"}
        />
        {(isNotYetProcuringSite ||
          (showNotYetImportingMessaging && isNotYetImportingSite)) && (
          <NonGeneratingSiteOverlay>
            <Column style={{ maxWidth: "570px" }}>
              <span
                style={{
                  fontSize: "30px",
                  color: "#757575",
                  margin: "0 15px 15px 15px",
                  padding: "0 8px",
                }}
              >
                Your site is not yet procuring energy
                {!isNotYetProcuringSite && <> from the InRange network</>}.
              </span>
              {buyEnergyBlock}
            </Column>
          </NonGeneratingSiteOverlay>
        )}
      </Chart>
    </ChartWrapper>
  );
};

export default ProposalSiteChart;

interface MonthlyTooltipProps {
  data: BarDatum;
  keys: string[];
  currency: boolean;
  currencyCode: string;
}

const MonthlyTooltip: React.FC<MonthlyTooltipProps> = ({
  data,
  keys,
  currency,
  currencyCode,
}) => {
  const formatValue = (key: string) => {
    const value =
      data[key] !== undefined
        ? Math.round(parseFloat(data[key].toString()) / 10) * 10
        : 0;
    return (
      <strong>
        {currency
          ? formatCurrency(value, currencyCode, 0)
          : formatUnits(value, "kWh")}
      </strong>
    );
  };

  return (
    <>
      {keys.map((key) => (
        <ChartToolTipBody key={key}>
          <ChartTooltipColorBox color={legendKeyColors[key]} />
          <span>{key + ": "}</span>
          {formatValue(key)}
        </ChartToolTipBody>
      ))}
    </>
  );
};

const ProposalChartHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding-right: 16px;
`;
